import Vue from "vue"
import { MutationTree, ActionTree, GetterTree } from "vuex"

import configToQs from "~/helpers/configToQs"
import resetMixin from "~/mixins/store/resetToInitialState"

import { RootState } from "~/store"
import Influencer from "~/types/influencer"
import InfluencerRecommendation from "~/types/influencerRecommendation"
import CampaignGoal from "~/types/campaignGoal"

const initialState = () => ({
  list: {} as Record<number, number | undefined>,
  ranks: [] as number[],
  topInfluencerCount: 0,
})
export const state = initialState
export type InfluencersRecommendationState = ReturnType<typeof state>

interface ApiResponse {
  top_influencer_count: number
  recommendations: InfluencerRecommendation[]
}

export const mutations: MutationTree<InfluencersRecommendationState> = {
  SET_LIST(state, payload: InfluencerRecommendation[]) {
    Vue.set(
      state,
      "ranks",
      payload.map((e) => e.influencer_id)
    )
    Vue.set(
      state,
      "list",
      payload.reduce((accumulator, recommendation) => {
        accumulator[recommendation.influencer_id] =
          recommendation.prediction_value
        return accumulator
      }, {} as Record<number, number>)
    )
  },
  MERGE_LIST(state, payload: InfluencerRecommendation[]) {
    payload.forEach((recommendation) => {
      Vue.set(
        state.list,
        recommendation.influencer_id,
        recommendation.prediction_value
      )
    })
  },
  SET_TOP_INFLUENCER_COUNT(state, payload: number) {
    state.topInfluencerCount = payload
  },
  ...resetMixin.mutations,
}

export const actions: ActionTree<InfluencersRecommendationState, RootState> = {
  FETCH_FROM_INFLUENCER_IDS(
    { state, commit, rootState, rootGetters },
    influencerIds: number[]
  ): Promise<void> {
    return new Promise((resolve, reject) => {
      if (rootGetters["user/IS_BAND"]) {
        const filteredSet: number[] = influencerIds.filter((id: number) => {
          return state.list[id] === undefined
        })

        if (filteredSet.length) {
          Promise.all(
            filteredSet
              .reduce(
                (accumulator, influencerId) => {
                  const lastMember: number[] =
                    accumulator[accumulator.length - 1]

                  if (lastMember.length >= 128) accumulator.push([] as number[])
                  accumulator[accumulator.length - 1].push(influencerId)
                  return accumulator
                },
                [[]] as number[][]
              )
              .map((influencerIdBatch) => {
                return this.$axios.$get(
                  `/recommendation/recommendation/?${configToQs({
                    //@ts-ignore
                    band_id: rootState.user.band.id,
                    influencer_ids: influencerIdBatch.join(","),
                  })}`
                )
              })
          )
            .then((responseBatch: ApiResponse[]) => {
              const response = responseBatch.reduce(
                (accumulator, response) => {
                  accumulator.recommendations.push(...response.recommendations)
                  accumulator.top_influencer_count =
                    response.top_influencer_count
                  return accumulator
                },
                { recommendations: [], top_influencer_count: 0 } as ApiResponse
              )

              commit(
                "MERGE_LIST",
                response.recommendations.map(
                  ({ influencer_id, prediction_value }) => ({
                    influencer_id,
                    prediction_value,
                  })
                )
              )
              resolve()
            })
            .catch((err) => {
              reject(err)
            })
        } else resolve()
      } else resolve()
    })
  },
  FETCH_RECOMMENDATION({ commit, rootState }): Promise<void> {
    //@ts-ignore
    const goalKeys: CampaignGoal[] = rootState.draft.goals

    return new Promise((resolve, reject) => {
      this.$axios
        .$get(
          `/recommendation/recommendation/?${configToQs({
            //@ts-ignore
            band_id: rootState.user.band.id,
            goals: goalKeys.join(","),
          })}`
        )
        .then(
          (response: {
            top_influencer_count: number
            recommendations: InfluencerRecommendation[]
          }) => {
            commit(
              "SET_LIST",
              response.recommendations.map(
                ({ influencer_id, prediction_value }) => ({
                  influencer_id,
                  prediction_value,
                })
              )
            )
            commit("SET_TOP_INFLUENCER_COUNT", response.top_influencer_count)
            resolve()
          }
        )
        .catch((err) => {
          reject(err)
        })
    })
  },
}

export const getters: GetterTree<InfluencersRecommendationState, RootState> = {
  GET_TOP_INFLUENCER_IDS: (state) =>
    state.ranks.slice(0, state.topInfluencerCount),
  GET_REMAINING_INFLUENCERS_IDS: (state) =>
    state.ranks.slice(state.topInfluencerCount),
  GET_BY_INFLUENCER_ID: (state) => (id: number) => state.list[id],
  INFLUENCER_ID_IS_IN_TOP(_state, getters) {
    return function (influencerId: number) {
      getters.GET_TOP_INFLUENCER_IDS.some((recommendation: Influencer) => {
        return recommendation.id === influencerId
      })
    }
  },
}
